import React from "react";
import "./reportCard.css";
import { DEFAULT_LAYOUT, STUDENT_FIELDS } from "./layoutDefaults";

/**
 * Renders ONE report card from the builder JSON (live preview or published snapshot).
 * The order / visibility / headings of blocks come from report.layout (or the `layout` prop).
 * Pure layout – nothing is calculated here.
 */
const fmt = (v) => (v === null || v === undefined || v === "" ? "" : Number.isInteger(v) ? v : Math.round(v * 100) / 100);

// 90.01–100 -> "91-100", 0–32 -> "32 & Below"
const legendRange = (g) => (Number(g.from) <= 0 ? `${Math.floor(Number(g.to))} & Below` : `${Math.ceil(Number(g.from))}-${Math.floor(Number(g.to))}`);

/* ------------------------------------------------------------------ blocks */

const HeaderBlock = ({ report, school, settings, block = {} }) => {
  const { student = {}, columns = [] } = report;
  const autoTitle = report.report_type === "EXAM_GROUP" ? `${columns[0]?.name || ""} Report Card` : "Annual Report Card";
  const titleLine = `${settings.title || autoTitle}${student.session_year ? ` · Session ${student.session_year}` : ""}`;
  // uploaded letterhead image replaces school name / address / logo
  if (block.mode === "IMAGE" && block.image) {
    return (
      <div className="rc-head-image">
        <img src={block.image} alt="" style={{ maxHeight: `${block.image_height || 120}px` }} />
        {block.show_title !== false && <div className="rc-title">{titleLine}</div>}
      </div>
    );
  }
  return (
    <div className="rc-head">
      {settings.show_logo && school.logo && (
        <img src={school.logo} alt="" className="rc-logo" onError={(e) => { e.target.style.display = "none"; }} />
      )}
      <div className="rc-head-text">
        {school.name && <div className="rc-school">{school.name}</div>}
        {settings.show_school_address && school.address && <div className="rc-address">{school.address}</div>}
        <div className="rc-title">
          {settings.title || autoTitle}{student.session_year ? ` · Session ${student.session_year}` : ""}
        </div>
      </div>
    </div>
  );
};

const StudentInfoBlock = ({ report, block }) => {
  const st = report.student || {};
  const values = {
    student_name: st.student_name,
    class: `${st.class_name || ""}${st.section_name ? ` - ${st.section_name}` : ""}`,
    father_name: st.father_name,
    mother_name: st.mother_name,
    sr_no: st.sr_no,
    roll_no: st.roll_no,
    date_of_birth: st.date_of_birth,
  };
  const keys = (block.fields && block.fields.length ? block.fields : DEFAULT_LAYOUT.blocks[1].fields)
    .filter((k) => STUDENT_FIELDS.some((f) => f.key === k));
  const pairs = keys.map((k) => [STUDENT_FIELDS.find((f) => f.key === k).label, values[k]]);
  const rows = [];
  for (let i = 0; i < pairs.length; i += 2) rows.push([pairs[i], pairs[i + 1]]);
  return (
    <>
      {block.title && <div className="rc-block-title">{block.title}</div>}
      <table className="rc-info">
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              <th>{r[0][0]}</th><td>{r[0][1]}</td>
              <th>{r[1]?.[0] || ""}</th><td>{r[1] ? r[1][1] : ""}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
};

const ScholasticBlock = ({ report, block }) => {
  const { columns = [], scholastic = {} } = report;
  const subjects = scholastic.subjects || [];
  const totals = scholastic.totals || { columns: {}, grand_total: {} };
  const showTP = block.show_theory_practical !== false;
  const showGT = block.show_grand_total !== false && columns.length > 1;

  const getTitleCell = (x, tid) => {
    if (!x) return null;
    if (x.titles) return x.titles[tid] || null;
    return x;
  };

  const calcTotal = (cKey, getter) => {
    return subjects.reduce(
      (acc, s) => {
        const v = getter(s.columns?.[cKey]);
        if (v && v.max != null) {
          acc.max += Number(v.max) || 0;
          acc.obt += Number(v.obt) || 0;
        }
        return acc;
      },
      { max: 0, obt: 0 }
    );
  };

  const cols = columns.map((c) => {
    const examTitles = (c.exam_titles && c.exam_titles.length) ? c.exam_titles : [{ id: c.key, name: c.name }];
    const titles = examTitles.map((t) => {
      // Collect all exam types (parts) present in title t across all subjects
      const partsMap = new Map();
      subjects.forEach((s) => {
        const tc = getTitleCell(s.columns?.[c.key], t.id);
        if (tc?.parts) {
          Object.entries(tc.parts).forEach(([pk, pv]) => {
            if (!pv.is_grade && pv.max > 0) {
              if (!partsMap.has(pk)) {
                partsMap.set(pk, {
                  key: pk,
                  name: pv.name || "Marks",
                  short_name: pv.short_name || "",
                  sort: pv.sort ?? 99,
                });
              }
            }
          });
        }
      });

      // Fallback for older snapshots without parts
      if (partsMap.size === 0) {
        const hasTheory = subjects.some((s) => {
          const cell = getTitleCell(s.columns?.[c.key], t.id);
          return cell?.has_theory === true || (cell?.theory && cell.theory.max > 0);
        });
        const hasPractical = subjects.some((s) => {
          const cell = getTitleCell(s.columns?.[c.key], t.id);
          return cell?.has_practical === true || (cell?.practical && cell.practical.max > 0);
        });
        if (hasTheory && hasPractical) {
          partsMap.set("theory", { key: "theory", name: "Theory", sort: 1 });
          partsMap.set("practical", { key: "practical", name: "Practical", sort: 2 });
        }
      }

      const sortedParts = Array.from(partsMap.values()).sort((a, b) => a.sort - b.sort || a.name.localeCompare(b.name));
      const hasSubCols = showTP && sortedParts.length > 1;

      let subCols = [];
      if (hasSubCols) {
        sortedParts.forEach((p) => {
          subCols.push({
            id: `${t.id}_${p.key}`,
            label: p.name,
            isTotal: false,
            cell: (x) => {
              const tc = getTitleCell(x, t.id);
              if (!tc) return null;
              if (tc.parts && tc.parts[p.key]) return tc.parts[p.key];
              if (p.key === "theory") return tc.theory;
              if (p.key === "practical") return tc.practical;
              return null;
            },
            absent: (x) => {
              const tc = getTitleCell(x, t.id);
              if (!tc) return false;
              if (tc.parts && tc.parts[p.key]) return tc.parts[p.key].absent;
              return tc.absent;
            },
            has: (x) => {
              const tc = getTitleCell(x, t.id);
              if (!tc) return false;
              if (tc.parts && tc.parts[p.key]) return tc.parts[p.key].max > 0;
              if (p.key === "theory") return tc.has_theory || (tc.theory && tc.theory.max > 0);
              if (p.key === "practical") return tc.has_practical || (tc.practical && tc.practical.max > 0);
              return false;
            },
            total: () => {
              const tt = totals.columns?.[c.key]?.titles?.[t.id];
              if (tt?.parts?.[p.key]) return tt.parts[p.key];
              if (p.key === "theory" && tt?.theory) return tt.theory;
              if (p.key === "practical" && tt?.practical) return tt.practical;
              return calcTotal(c.key, (x) => {
                const tc = getTitleCell(x, t.id);
                if (tc?.parts?.[p.key]) return tc.parts[p.key];
                if (p.key === "theory") return tc?.theory;
                if (p.key === "practical") return tc?.practical;
                return null;
              });
            },
          });
        });

        // Title Total
        subCols.push({
          id: `${t.id}_total`,
          label: "Total",
          isTotal: true,
          cell: (x) => getTitleCell(x, t.id)?.total || getTitleCell(x, t.id),
          absent: (x) => getTitleCell(x, t.id)?.absent,
          has: (x) => !!getTitleCell(x, t.id),
          total: () => totals.columns?.[c.key]?.titles?.[t.id] || calcTotal(c.key, (x) => getTitleCell(x, t.id)?.total || getTitleCell(x, t.id)),
        });
      } else {
        subCols = [
          {
            id: `${t.id}_single`,
            label: t.name,
            isTotal: false,
            cell: (x) => getTitleCell(x, t.id)?.total || getTitleCell(x, t.id)?.theory || getTitleCell(x, t.id),
            absent: (x) => getTitleCell(x, t.id)?.absent,
            has: (x) => !!getTitleCell(x, t.id),
            total: () => totals.columns?.[c.key]?.titles?.[t.id] || calcTotal(c.key, (x) => getTitleCell(x, t.id)?.total || getTitleCell(x, t.id)),
          },
        ];
      }
      return { id: t.id, name: t.name, hasSubCols, subCols };
    });

    const hasMultipleTitles = examTitles.length > 1;
    const hasTermTotalCol = hasMultipleTitles;
    const leafCols = [];
    titles.forEach((t, tIdx) => {
      t.subCols.forEach((sc, scIdx) => {
        leafCols.push({
          ...sc,
          titleId: t.id,
          isGStart: tIdx === 0 && scIdx === 0 ? true : scIdx === 0,
        });
      });
    });
    if (hasTermTotalCol) {
      leafCols.push({
        id: `${c.key}_term_total`,
        label: "Total",
        isTermTotal: true,
        isGStart: true,
        cell: (x) => x?.total,
        absent: (x) => x?.absent,
        has: (x) => !!x?.total,
        total: () => totals.columns?.[c.key],
      });
    }

    const totalColSpan = leafCols.length * 2 + 1; // 2 for each leafCol (Max/Obt) + 1 for Grade
    return {
      ...c,
      titles,
      hasMultipleTitles,
      hasTermTotalCol,
      leafCols,
      totalColSpan,
    };
  });

  const anyHasSubParts = cols.some((c) => c.titles.some((t) => t.hasSubCols));
  const obtCell = (v, absent) => (v ? (absent && !v.obt ? "AB" : fmt(v.obt)) : "");

  return (
    <>
      <div className="rc-table-wrapper">
        <table className="rc-grid rc-marks">
          <thead>
            {/* Row 1: Term / Exam Group Header */}
            <tr>
              <th rowSpan={anyHasSubParts ? 4 : 3} className="rc-left rc-subject-head">
                {block.title || "Scholastic Area"}<br /><span className="rc-sub">Subject Name</span>
              </th>
              {cols.map((c) => (
                <th key={c.key} colSpan={c.totalColSpan} className="rc-gstart rc-term">
                  {c.name}
                </th>
              ))}
              {showGT && (
                <th colSpan={3} rowSpan={anyHasSubParts ? 3 : 2} className="rc-gstart rc-term">
                  Grand Total
                </th>
              )}
            </tr>

            {/* Row 2: Exam Titles & Term Total / Grade */}
            <tr>
              {cols.map((c) => (
                <React.Fragment key={c.key}>
                  {c.titles.map((t, i) => (
                    <th
                      key={t.id}
                      colSpan={t.hasSubCols ? t.subCols.length * 2 : 2}
                      rowSpan={t.hasSubCols ? 1 : anyHasSubParts ? 2 : 1}
                      className={i === 0 ? "rc-gstart" : ""}
                    >
                      {t.name}
                    </th>
                  ))}
                  {c.hasTermTotalCol && (
                    <th colSpan={2} rowSpan={anyHasSubParts ? 2 : 1} className="rc-gstart rc-strong">
                      Total
                    </th>
                  )}
                  <th rowSpan={anyHasSubParts ? 3 : 2} className="rc-grade-head">
                    Grade
                  </th>
                </React.Fragment>
              ))}
            </tr>

            {/* Row 3: Sub-columns (Theory / Practical / Total) - only if any exam title has sub-columns */}
            {anyHasSubParts && (
              <tr>
                {cols.map((c) => (
                  <React.Fragment key={c.key}>
                    {c.titles.map((t) =>
                      t.hasSubCols
                        ? t.subCols.map((sc, sci) => (
                            <th
                              key={sc.id}
                              colSpan={2}
                              className={`${sci === 0 ? "rc-gstart " : ""}${sc.isTotal ? "rc-strong" : ""}`}
                            >
                              {sc.label}
                            </th>
                          ))
                        : null
                    )}
                  </React.Fragment>
                ))}
              </tr>
            )}

            {/* Row 4: Max / Obt */}
            <tr>
              {cols.map((c) => (
                <React.Fragment key={c.key}>
                  {c.leafCols.map((l) => (
                    <React.Fragment key={l.id}>
                      <th className={`rc-mo${l.isGStart ? " rc-gstart" : ""}`}>Max</th>
                      <th className="rc-mo">Obt.</th>
                    </React.Fragment>
                  ))}
                </React.Fragment>
              ))}
              {showGT && (
                <>
                  <th className="rc-mo rc-gstart">Max</th>
                  <th className="rc-mo">Obt.</th>
                  <th className="rc-grade-head">Grade</th>
                </>
              )}
            </tr>
          </thead>

          <tbody>
            {subjects.map((s) => (
              <tr key={s.name}>
                <td className="rc-left rc-subject">{s.name}</td>
                {cols.map((c) => {
                  const x = s.columns?.[c.key];
                  return (
                    <React.Fragment key={c.key}>
                      {c.leafCols.map((l) => {
                        const v = l.has(x) ? l.cell(x) : null;
                        return (
                          <React.Fragment key={l.id}>
                            <td className={`rc-max${l.isGStart ? " rc-gstart" : ""}`}>
                              {v && v.max != null && v.max > 0 ? fmt(v.max) : "–"}
                            </td>
                            <td className={l.isTotal || l.isTermTotal ? "rc-strong" : ""}>
                              {v ? obtCell(v, l.absent(x)) : "–"}
                            </td>
                          </React.Fragment>
                        );
                      })}
                      <td className="rc-grade">{x?.grade || ""}</td>
                    </React.Fragment>
                  );
                })}
                {showGT && (
                  <>
                    <td className="rc-max rc-gstart">{s.grand_total ? fmt(s.grand_total.total.max) : ""}</td>
                    <td className="rc-strong">{s.grand_total ? fmt(s.grand_total.total.obt) : ""}</td>
                    <td className="rc-grade">{s.grand_total?.grade || ""}</td>
                  </>
                )}
              </tr>
            ))}

            <tr className="rc-total">
              <td className="rc-left">Total</td>
              {cols.map((c) => {
                const termTot = totals.columns?.[c.key];
                return (
                  <React.Fragment key={c.key}>
                    {c.leafCols.map((l) => {
                      const pt = l.total();
                      return (
                        <React.Fragment key={l.id}>
                          <td className={l.isGStart ? "rc-gstart" : ""}>
                            {pt && pt.max != null ? fmt(pt.max) : ""}
                          </td>
                          <td>{pt && pt.obt != null ? fmt(pt.obt) : ""}</td>
                        </React.Fragment>
                      );
                    })}
                    <td className="rc-grade">{termTot?.grade || ""}</td>
                  </React.Fragment>
                );
              })}
              {showGT && (
                <>
                  <td className="rc-gstart">{fmt(totals.grand_total?.max)}</td>
                  <td>{fmt(totals.grand_total?.obt)}</td>
                  <td className="rc-grade">{totals.grand_total?.grade || ""}</td>
                </>
              )}
            </tr>
          </tbody>
        </table>
      </div>

      {(block.show_percentage !== false || block.show_result !== false) && totals.grand_total?.percentage != null && (
        <div className="rc-summary-boxes">
          <div className="rc-box">
            <span>Marks Obtained</span>
            <b>{fmt(totals.grand_total.obt)} / {fmt(totals.grand_total.max)}</b>
          </div>
          {block.show_percentage !== false && (
            <div className="rc-box">
              <span>Percentage</span>
              <b>{fmt(totals.grand_total.percentage)}%</b>
            </div>
          )}
          {totals.grand_total.grade && (
            <div className="rc-box">
              <span>Overall Grade</span>
              <b>{totals.grand_total.grade}</b>
            </div>
          )}
          {block.show_result !== false && report.result_status && (
            <div className={`rc-box rc-result-${String(report.result_status).toLowerCase()}`}>
              <span>Result</span>
              <b>{report.result_status}</b>
            </div>
          )}
        </div>
      )}
    </>
  );
};

const OptionalSubjectBlock = ({ report, block }) => {
  const opt = report.optional_subjects || {};
  const subjects = opt.subjects || [];
  // If no optional subject has text entered for this student, do NOT show this table
  if (!subjects.length) return null;
  return (
    <div className="rc-table-wrapper">
      <table className="rc-grid rc-optional">
        <thead>
          <tr>
            <th className="rc-left">{block.title || "Optional Subject"}</th>
            {(opt.columns || []).map((c) => (
              <th key={c.key}>{c.name}<br />Grade / Remarks</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {subjects.map((s) => (
            <tr key={s.id || s.name}>
              <td className="rc-left rc-subject">{s.name}</td>
              {(opt.columns || []).map((c) => (
                <td key={c.key} className="rc-strong">{s.values?.[c.key] || "–"}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const CoScholasticBlock = ({ report, block }) => {
  const co = report.co_scholastic || {};
  if (!(co.subjects || []).length) return null;
  return (
    <div className="rc-table-wrapper">
      <table className="rc-grid rc-cosch">
        <thead>
          <tr>
            <th className="rc-left">{block.title || "Co-Scholastic Area"}</th>
            {(co.columns || []).map((c) => <th key={c.key}>{c.name}<br />Grade</th>)}
          </tr>
        </thead>
        <tbody>
          {co.subjects.map((s) => (
            <tr key={s.id}>
              <td className="rc-left">{s.name}</td>
              {(co.columns || []).map((c) => <td key={c.key}><b>{s.grades?.[c.key] || ""}</b></td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const CustomTableBlock = ({ block }) => {
  const headers = Array.isArray(block.headers) ? block.headers : [];
  const rows = Array.isArray(block.rows) ? block.rows : [];
  if (!headers.length && !rows.length) return null;
  return (
    <div className="rc-table-wrapper">
      {block.title && <div className="rc-block-title">{block.title}</div>}
      <table className="rc-grid rc-custom-table">
        {headers.length > 0 && (
          <thead>
            <tr>
              {headers.map((h, i) => (
                <th key={i}>{h}</th>
              ))}
            </tr>
          </thead>
        )}
        <tbody>
          {rows.map((r, ri) => (
            <tr key={ri}>
              {headers.map((_, ci) => (
                <td key={ci}>{r[ci] ?? ""}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const FooterBlock = ({ report, block }) => {
  const footer = report.footer || [];
  if (!footer.length) return null;
  return (
    <>
      {block.title && <div className="rc-block-title">{block.title}</div>}
      <div className="rc-table-wrapper">
        <table className="rc-footer">
          <tbody>
            {footer.map((f) => (
              <tr key={f.field_key}>
                <th>{f.title}</th>
                <td className={f.input_type === "TEXTAREA" ? "rc-remark" : ""}>{f.value ?? ""}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
};

const SignaturesBlock = ({ report, block }) => {
  const labels = (block.labels || []).filter((l) => l !== undefined);
  const date = report.issue_date ? new Date(report.issue_date).toLocaleDateString("en-GB") : "";
  return (
    <div className="rc-sign">
      {labels.map((l, i) => (
        <div key={i}>{/^date$/i.test(l.trim()) ? `Date: ${date}` : l}</div>
      ))}
    </div>
  );
};

const LegendBlock = ({ report, block, settings }) => {
  const gl = report.grade_legend || {};
  const showS = block.show_scholastic !== false && (gl.scholastic || []).length > 0;
  const showC = block.show_co_scholastic !== false && (gl.co_scholastic || []).length > 0;
  if (!showS && !showC && !block.title) return null;

  const scaleSize = Number(block.scale_size || settings?.legend_scale_size) || 100;
  const scaleFactor = Math.min(1, Math.max(0.5, scaleSize / 100));
  const isCompact = scaleSize < 100;

  const legendStyle = isCompact ? {
    fontSize: `${Math.max(7.5, Math.round(9 * scaleFactor * 10) / 10)}px`,
    paddingTop: `${Math.round(4 * scaleFactor)}px`,
  } : {};

  const cellStyle = isCompact ? {
    padding: `${Math.max(1, Math.round(2 * scaleFactor))}px ${Math.max(2, Math.round(5 * scaleFactor))}px`,
  } : {};

  return (
    <div className="rc-legend" style={legendStyle}>
      {block.title && <div className="rc-legend-title" style={isCompact ? { marginBottom: 2 } : {}}>{block.title}</div>}
      <div className="rc-legend-tables" style={isCompact ? { gap: `${Math.round(10 * scaleFactor)}px` } : {}}>
        {showS && (
          <table style={isCompact ? { minWidth: `${Math.round(130 * scaleFactor)}px`, marginBottom: 4 } : {}}>
            <thead><tr><th colSpan={2} style={cellStyle}>Scholastic</th></tr><tr><th style={cellStyle}>Marks Range</th><th style={cellStyle}>Grade</th></tr></thead>
            <tbody>{gl.scholastic.map((g) => <tr key={g.grade}><td style={cellStyle}>{legendRange(g)}</td><td style={cellStyle}>{g.grade}</td></tr>)}</tbody>
          </table>
        )}
        {showC && (
          <table style={isCompact ? { minWidth: `${Math.round(130 * scaleFactor)}px`, marginBottom: 4 } : {}}>
            <thead><tr><th colSpan={2} style={cellStyle}>Co-Scholastic</th></tr><tr><th style={cellStyle}>Marks Range</th><th style={cellStyle}>Grade</th></tr></thead>
            <tbody>{gl.co_scholastic.map((g) => <tr key={g.grade}><td style={cellStyle}>{legendRange(g)}</td><td style={cellStyle}>{g.grade}</td></tr>)}</tbody>
          </table>
        )}
      </div>
    </div>
  );
};

const TextBlock = ({ block }) =>
  block.text ? (
    <div className="rc-text" style={{ textAlign: block.align || "left", fontWeight: block.bold ? 600 : 400 }}>
      {block.text}
    </div>
  ) : null;

const SpacerBlock = ({ block }) => <div style={{ height: `${block.height || 10}px` }} />;

const RENDERERS = {
  HEADER: HeaderBlock,
  STUDENT_INFO: StudentInfoBlock,
  SCHOLASTIC: ScholasticBlock,
  OPTIONAL_SUBJECT: OptionalSubjectBlock,
  CO_SCHOLASTIC: CoScholasticBlock,
  FOOTER: FooterBlock,
  SIGNATURES: SignaturesBlock,
  GRADE_LEGEND: LegendBlock,
  TEXT: TextBlock,
  SPACER: SpacerBlock,
  TABLE: CustomTableBlock,
};

/* ------------------------------------------------------------------ card */

const ReportCardView = ({ report, school = {}, layout: layoutProp, highlightId }) => {
  if (!report) return null;
  const layout = layoutProp || report.layout || DEFAULT_LAYOUT;
  const settings = { ...DEFAULT_LAYOUT.settings, ...(layout.settings || {}) };

  const hasBorder = settings.show_border !== false && settings.border_style !== "none";
  const borderStyle = hasBorder
    ? `${settings.border_width ?? 3}px ${settings.border_style || "double"} ${settings.border_color || "#1d3557"}`
    : "none";

  const noColor = settings.table_header_no_color;
  const thBg = noColor ? "#ffffff" : (settings.table_header_bg || "#e9eff8");
  const thColor = noColor ? "#000000" : (settings.table_header_color || "#1d3557");
  const thSubBg = noColor ? "#ffffff" : (settings.table_header_bg ? `${settings.table_header_bg}d9` : "#d6e2f3");

  const pageStyle = {
    fontSize: `${settings.font_size || 11}px`,
    border: borderStyle,
    borderRadius: hasBorder ? `${settings.border_radius ?? 4}px` : "0px",
    "--rc-th-bg": thBg,
    "--rc-th-color": thColor,
    "--rc-th-sub-bg": thSubBg,
  };

  return (
    <div className="rc-page" style={pageStyle}>
      {(layout.blocks || []).filter((b) => b.visible !== false).map((b) => {
        const R = RENDERERS[b.type];
        if (!R) return null;
        return (
          <div key={b.id} className={`rc-block${highlightId === b.id ? " rc-highlight" : ""}`}>
            <R report={report} school={school} settings={settings} block={b} />
          </div>
        );
      })}
      {report.version && <div className="rc-version">v{report.version}</div>}
    </div>
  );
};

export default ReportCardView;
